import React, { Fragment, useMemo } from 'react';

import { ShortcutsSection } from '@confluence/space-shortcuts/entry-points/shortcutsSection.ts';
import { SpaceViewsController } from '@confluence/space-views';
import { PageTreeLoaderOnHover } from '@confluence/page-tree/entry-points/PageTreeLoader.tsx';
import { BlogTree } from '@confluence/blog-tree/entry-points/BlogTree.tsx';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import { EXTERNAL_PRODUCT_CONNECTION } from './types/RemoteObject.ts';
import { SpaceNavigationRelatedContent } from './SpaceNavigationRelatedContent.tsx';
import { ThirdPartyAuthentication } from './ThirdPartyAuthentication.tsx';

export const CommonSpaceNavigationContent = ({
	showShortcutsSection,
	space,
	spaceKey,
	quick,
	homepageId,
	loading,
	isSpaceAdmin,
	isBlogNavigation,
	blogsHidden,
	pageTreeFinishedLoading,
	onDragDropSuccess,
	onContentTreeLoadComplete,
	spaceName,
	iconPath,
}) => {
	const { cloudId } = useSessionData();
	const authResult = useFetch3PAuthentications({
		products: [
			EXTERNAL_PRODUCT_CONNECTION.LOOM,
			EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS,
			EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
		],
		cloudId,
	});
	const allUnauthenticated = useMemo(
		() =>
			!authResult.isLoading &&
			Object.values(authResult.authentications).length > 0 &&
			Object.values(authResult.authentications).every((status) => !status.isAuthenticated),
		[authResult],
	);

	return (
		<>
			{showShortcutsSection && (
				<ShortcutsSection
					isSpaceAdmin={isSpaceAdmin}
					spaceKey={spaceKey}
					links={quick}
					spaceId={space?.id || null}
				/>
			)}

			{!loading ? (
				<div
					data-vc="pageTree"
					data-testid="pageTree"
					onMouseEnter={
						expVal('confluence_frontend_remove_hydrateonhover_pagetree', 'isEnabled', false)
							? undefined
							: PageTreeLoaderOnHover.hydrateOnHover
					}
				>
					<SpaceViewsController
						key={spaceKey} // Remount on space change to reset Space views state
						homepageId={homepageId}
						onDragDropSuccess={onDragDropSuccess}
						isPeekingFromBlogs={isBlogNavigation}
						onContentTreeLoadComplete={onContentTreeLoadComplete}
						spaceHasUserContent={space?.didContainUserContent}
						pageTreeFinishedLoading={pageTreeFinishedLoading}
						relatedContent={
							allUnauthenticated ? (
								<ThirdPartyAuthentication />
							) : (
								<SpaceNavigationRelatedContent spaceName={spaceName} iconPath={iconPath} />
							)
						}
					/>
				</div>
			) : (
				<Fragment />
			)}

			{!loading && !blogsHidden && (
				<BlogTree spaceKey={spaceKey} pageTreeFinishedLoading={pageTreeFinishedLoading} />
			)}
		</>
	);
};
